import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, List, Button, Modal, Form, Input, message, Tabs, Space, Select, Spin } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  fetchArtifactTypes,
  createArtifactType,
  updateArtifactType,
  deleteArtifactType,
} from '../../store/slices/artifactTypeSlice';
import { RootState, AppDispatch } from '../../store';
import { ArtifactType } from '../../types/artifactTypes';
import { apiClient } from '../../services/apiClient';
import { ArtifactService } from '../../services/api/artifactService';
import { useUser } from '../../contexts/UserContext';
import { resolveTreeForModelSlug } from '../../utils/resolveTreeForModelSlug';
import './ArtifactTypeList.css';

const { TextArea } = Input;
const { Option } = Select;

export const ArtifactTypeList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { items, loading, error } = useSelector((state: RootState) => state.artifactTypes);
  const { selectedCompany, availableCompanies } = useUser();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<ArtifactType | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editForm] = Form.useForm();
  
  // Prime artifacts state
  const [primeArtifacts, setPrimeArtifacts] = useState<any[]>([]);
  const [loadingPrimeArtifacts, setLoadingPrimeArtifacts] = useState(false);
  
  // Company artifacts state
  const [selectedTreeId, setSelectedTreeId] = useState<string | null>(null);
  const [artifactTrees, setArtifactTrees] = useState<any[]>([]);
  const [companyArtifacts, setCompanyArtifacts] = useState<any[]>([]);
  const [loadingCompanyArtifacts, setLoadingCompanyArtifacts] = useState(false);
  const [loadingTrees, setLoadingTrees] = useState(false);

  useEffect(() => {
    void dispatch(fetchArtifactTypes());
  }, [dispatch]);
  
  useEffect(() => {
    if (selectedType) {
      void fetchPrimeArtifacts(selectedType.id);
    }
  }, [selectedType]);
  
  useEffect(() => {
    setSelectedTreeId(null);
    void fetchTrees();
  }, [selectedCompany?.id]);
  
  useEffect(() => {
    if (selectedType && selectedTreeId) {
      void fetchCompanyArtifacts(selectedType, selectedTreeId);
    } else {
      setCompanyArtifacts([]);
    }
  }, [selectedType, selectedTreeId, selectedCompany?.id]);

  const fetchPrimeArtifacts = async (typeId: string) => {
    setLoadingPrimeArtifacts(true);
    setPrimeArtifacts([]);
    try {
      let allArtifacts: any[] = [];
      let skip = 0;
      const limit = 100;
      let hasMore = true;

      while (hasMore) {
        const response = await apiClient.get(
          `/prime-artifacts/?skip=${skip}&limit=${limit}&artifact_type=${encodeURIComponent(typeId)}`
        );
        const artifacts = response.data || [];
        allArtifacts = [...allArtifacts, ...artifacts];
        if (artifacts.length < limit) {
          hasMore = false;
        } else {
          skip += limit;
        }
      }

      setPrimeArtifacts(allArtifacts);
    } catch (error: any) {
      console.error('Error fetching prime artifacts:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error';
      message.error(`Failed to fetch prime artifacts: ${errorMessage}`);
      setPrimeArtifacts([]);
    } finally {
      setLoadingPrimeArtifacts(false);
    }
  };
  
  const fetchTrees = async () => {
    if (!selectedCompany?.id) {
      setArtifactTrees([]);
      setSelectedTreeId(null);
      return;
    }
    
    setLoadingTrees(true);
    try {
      const accessibleIds = availableCompanies.map(uc => uc.company.id);
      const trees = await ArtifactService.getTrees(selectedCompany.id, accessibleIds);
      setArtifactTrees(trees);
      const slug = selectedCompany.kryspace_slug?.trim();
      const preferred = slug
        ? resolveTreeForModelSlug(trees, slug, { companyId: selectedCompany.id })
        : undefined;
      const defaultTree = preferred ?? trees[0];
      if (defaultTree) {
        setSelectedTreeId(defaultTree.id);
      } else {
        setSelectedTreeId(null);
      }
    } catch (error) {
      console.error('Error fetching artifact trees:', error);
      message.error('Failed to fetch artifact trees');
    } finally {
      setLoadingTrees(false);
    }
  };
  
  const fetchCompanyArtifacts = async (
    type: ArtifactType,
    treeId: string | null
  ) => {
    if (!treeId) {
      setCompanyArtifacts([]);
      return;
    }
    
    setLoadingCompanyArtifacts(true);
    setCompanyArtifacts([]); // Clear previous results
    try {
      let allArtifacts: any[] = [];
      let skip = 0;
      const limit = 100;
      let hasMore = true;
      let errorOccurred = false;
      
      while (hasMore && !errorOccurred) {
        try {
          const params = new URLSearchParams();
          params.append('tree_id', treeId);
          if (selectedCompany?.id) {
            params.append('company_id', selectedCompany.id);
          }
          params.append('artifact_type', type.id);
          params.append('offset', skip.toString());
          params.append('limit', limit.toString());

          const response = await apiClient.get(`/artifacts?${params.toString()}`);
          const artifacts = response.data || [];
          
          allArtifacts = [...allArtifacts, ...artifacts];
          
          if (artifacts.length < limit) {
            hasMore = false;
          } else {
            skip += limit;
          }
        } catch (pageError: any) {
          console.error('Error fetching company artifacts page:', pageError);
          errorOccurred = true;
          if (allArtifacts.length > 0) {
            console.warn('Using partial results from company artifacts fetch');
            break;
          } else {
            throw pageError;
          }
        }
      }
      
      setCompanyArtifacts(allArtifacts);
      
      if (errorOccurred && allArtifacts.length > 0) {
        message.warning('Some company artifacts could not be loaded. Showing partial results.');
      }
    } catch (error: any) {
      console.error('Error fetching company artifacts:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error';
      message.error(`Failed to fetch company artifacts: ${errorMessage}`);
      setCompanyArtifacts([]);
    } finally {
      setLoadingCompanyArtifacts(false);
    }
  };

  const handleEdit = (type: ArtifactType) => {
    setEditingId(type.id);
    editForm.setFieldsValue({
      name: type.name,
      description: type.description
    });
  };

  const handleSave = async (values: any) => {
    if (editingId && selectedType) {
      try {
        await dispatch(updateArtifactType({ id: editingId, data: values }));
        message.success('Artifact type updated');
        setEditingId(null);
        editForm.resetFields();
      } catch (error) {
        console.error('Error updating type:', error);
        message.error('Failed to update artifact type');
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this type?')) {
      await dispatch(deleteArtifactType(id));
    }
  };

  const handleCreate = () => {
    // Create a temporary type object for the list
    const tempId = `temp-${Date.now()}`;
    const newType: ArtifactType = {
      id: tempId,
      name: '',
      description: '',
      created_at: new Date().toISOString(),
    };
    
    // Set as selected and show form
    setSelectedType(newType);
    setIsCreating(true);
    setEditingId(tempId);
    editForm.resetFields();
  };

  const handleSaveNew = async (values: any) => {
    try {
      const result = await dispatch(createArtifactType(values));
      message.success('Artifact type created');
      setIsCreating(false);
      setEditingId(null);
      // Redux will update the items list, find the new type
      // We need to wait for Redux to update, so reload or find in current items
      const newType = items.find(t => t.name === values.name);
      if (newType) {
        setSelectedType(newType);
      }
      editForm.resetFields();
    } catch (error) {
      console.error('Error creating type:', error);
      message.error('Failed to create artifact type');
      throw error;
    }
  };

  const handleCancelNew = () => {
    setIsCreating(false);
    setEditingId(null);
    setSelectedType(null);
    editForm.resetFields();
  };


  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  // Sort artifact types alphabetically by name
  const sortedItems = [...items].sort((a, b) => 
    a.name.toLowerCase().localeCompare(b.name.toLowerCase())
  );

  return (
    <div className="artifact-types-enhanced">
      <div className="type-list-section">
        <Card
          title="Artifact Types"
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              New
            </Button>
          }
        >
          <List
            dataSource={sortedItems}
            loading={loading}
            renderItem={(type) => (
              <List.Item
                className={selectedType?.id === type.id ? 'selected' : ''}
                onClick={() => setSelectedType(type)}
                style={{ cursor: 'pointer' }}
              >
                <List.Item.Meta 
                  title={type.name} 
                />
              </List.Item>
            )}
          />
        </Card>
      </div>

      <div className="type-details-section">
        {selectedType ? (
          <Tabs 
            defaultActiveKey="details"
            items={[
              {
                key: 'details',
                label: 'Details',
                children: (
                  <Card 
                    title="Type Details"
                    extra={
                      <Space>
                        {!isCreating && (
                          <>
                            <Button 
                              icon={<EditOutlined />} 
                              onClick={() => handleEdit(selectedType)}
                              disabled={editingId === selectedType.id}
                            >
                              {editingId === selectedType.id ? 'Editing...' : 'Edit'}
                            </Button>
                            <Button 
                              danger 
                              icon={<DeleteOutlined />} 
                              onClick={() => {
                                Modal.confirm({
                                  title: 'Delete Artifact Type',
                                  content: `Are you sure you want to delete "${selectedType.name}"? This action cannot be undone.`,
                                  okText: 'Delete',
                                  okType: 'danger',
                                  onOk: () => handleDelete(selectedType.id),
                                });
                              }}
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </Space>
                    }
                  >
                    {(editingId === selectedType.id || isCreating) ? (
                      <Form 
                        form={editForm} 
                        layout="vertical" 
                        onFinish={isCreating ? handleSaveNew : handleSave}
                      >
                        <Form.Item
                          label="Name"
                          name="name"
                          rules={[{ required: true, message: 'Please enter type name' }]}
                        >
                          <Input placeholder="e.g., Financial, Entity, Plan" />
                        </Form.Item>
                        <Form.Item label="Description" name="description">
                          <TextArea rows={3} placeholder="Describe this artifact type" />
                        </Form.Item>
                        <Form.Item>
                          <Space>
                            <Button type="primary" htmlType="submit">
                              {isCreating ? 'Create' : 'Save Changes'}
                            </Button>
                            <Button onClick={isCreating ? handleCancelNew : () => {
                              setEditingId(null);
                              setIsCreating(false);
                              editForm.resetFields();
                            }}>
                              Cancel
                            </Button>
                          </Space>
                        </Form.Item>
                      </Form>
                    ) : (
                      <div>
                        <p><strong>Name:</strong> {selectedType.name}</p>
                        <p><strong>Description:</strong> {selectedType.description || 'No description'}</p>
                        <p><strong>ID:</strong> {selectedType.id}</p>
                      </div>
                    )}
                  </Card>
                )
              },
              {
                key: 'prime-artifacts',
                label: 'Prime Artifacts',
                children: (
                  <Card title={`Prime Artifacts (${primeArtifacts.length})`}>
                    <Spin spinning={loadingPrimeArtifacts}>
                      {primeArtifacts.length > 0 ? (
                        <List
                          dataSource={primeArtifacts}
                          renderItem={(artifact) => (
                            <List.Item>
                              <List.Item.Meta
                                title={artifact.name}
                                description={artifact.description || 'No description'}
                              />
                            </List.Item>
                          )}
                        />
                      ) : (
                        <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                          {loadingPrimeArtifacts ? 'Loading...' : 'No prime artifacts found for this type'}
                        </p>
                      )}
                    </Spin>
                  </Card>
                )
              },
              {
                key: 'company-artifacts',
                label: 'Company Artifacts',
                children: (
                  <Card title={`Company Artifacts (${companyArtifacts.length})`}>
                    {!selectedCompany ? (
                      <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                        Please select a company to view company artifacts
                      </p>
                    ) : (
                      <div>
                        <div style={{ marginBottom: 16 }}>
                          <label style={{ display: 'block', marginBottom: 8 }}>
                            <strong>Artifact Tree:</strong>
                          </label>
                          <Select
                            value={selectedTreeId}
                            onChange={setSelectedTreeId}
                            loading={loadingTrees}
                            style={{ width: '100%', maxWidth: 400 }}
                            placeholder="Select artifact tree"
                          >
                            {artifactTrees.map((tree) => (
                              <Option key={tree.id} value={tree.id}>
                                {tree.name}
                              </Option>
                            ))}
                          </Select>
                        </div>
                        <Spin spinning={loadingCompanyArtifacts}>
                          {companyArtifacts.length > 0 ? (
                            <List
                              dataSource={companyArtifacts}
                              renderItem={(artifact) => (
                                <List.Item>
                                  <List.Item.Meta
                                    title={artifact.name}
                                    description={artifact.description || 'No description'}
                                  />
                                </List.Item>
                              )}
                            />
                          ) : (
                            <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                              {loadingCompanyArtifacts 
                                ? 'Loading...' 
                                : selectedTreeId 
                                  ? 'No company artifacts found for this type in the selected tree'
                                  : 'Please select an artifact tree'}
                            </p>
                          )}
                        </Spin>
                      </div>
                    )}
                  </Card>
                )
              }
            ]}
          />
        ) : (
          <Card>
            <p>Select an artifact type to view its details</p>
          </Card>
        )}
      </div>
    </div>
  );
};
